import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import CtaBand from "@/components/feature/CtaBand";
import Reveal from "@/components/base/Reveal";
import { journeyTimeline } from "@/mocks/journeys";
import type { JourneyStop } from "@/types/content";

interface JourneyStopDetailProps {
  stop: JourneyStop;
}

export default function JourneyStopDetail({ stop }: JourneyStopDetailProps) {
  const isTbc = stop.status === "tbc";
  const index = journeyTimeline.findIndex((s) => s.slug === stop.slug);
  const next = journeyTimeline[(index + 1) % journeyTimeline.length];

  return (
    <SiteLayout>
      <section className="relative flex min-h-[70vh] items-end overflow-hidden bg-foreground-950 text-background-50">
        {stop.image && (
          <>
            <img
              src={stop.image}
              alt={`${stop.destination} — Chef Eddie & Kiki G`}
              title={`${stop.destination} culinary journey`}
              className="absolute inset-0 h-full w-full object-cover object-center animate-kenburns"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-foreground-950 via-foreground-950/70 to-foreground-950/30" />
          </>
        )}
        {!stop.image && (
          <div
            className="absolute inset-0 opacity-[0.10]"
            style={{
              backgroundImage:
                "radial-gradient(oklch(var(--background-50)) 1px, transparent 1px)",
              backgroundSize: "30px 30px",
            }}
            aria-hidden="true"
          />
        )}

        <div className="relative z-10 w-full px-5 md:px-10 lg:px-16 pb-16 md:pb-24">
          <div className="max-w-4xl">
            <Reveal>
              <p className="eyebrow text-primary-400">
                {stop.season} · {stop.fullDate}
              </p>
            </Reveal>
            <Reveal delay={120}>
              <h1
                className={`mt-5 font-heading text-5xl leading-[0.98] sm:text-6xl md:text-7xl lg:text-8xl ${
                  isTbc ? "italic text-background-50/80" : ""
                }`}
              >
                {stop.destination}
              </h1>
            </Reveal>
            {stop.note && (
              <Reveal delay={200}>
                <p className="mt-4 font-heading text-xl italic text-background-50/85 md:text-2xl">
                  {stop.note}
                </p>
              </Reveal>
            )}
            <Reveal delay={280}>
              <div className="mt-9 flex flex-col gap-4 sm:flex-row sm:items-center">
                <Link
                  to="/invite-eddie"
                  className="btn-primary"
                >
                  Invite Eddie
                  <i className="ri-send-plane-line text-base" />
                </Link>
                <Link
                  to="/hire-eddie"
                  className="btn-ghost text-background-50/85 hover:bg-background-50 hover:text-foreground-950"
                >
                  Hire Eddie
                </Link>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <div className="border-b border-foreground-950/10 bg-background-50">
        <div className="w-full px-5 md:px-10 lg:px-16">
          <div className="grid grid-cols-2 gap-6 py-7 md:grid-cols-4">
            {[
              { k: "Date", v: stop.fullDate },
              { k: "Destination", v: stop.destination },
              { k: "Region", v: stop.region ?? "—" },
              {
                k: "Status",
                v: isTbc ? "To be confirmed" : "Confirmed",
              },
            ].map((item) => (
              <div key={item.k}>
                <div className="font-label text-[10px] uppercase tracking-label text-foreground-500">
                  {item.k}
                </div>
                <div className="mt-2 font-body text-sm text-foreground-900">
                  {item.v}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <section className="bg-background-50">
        <div className="w-full px-5 md:px-10 lg:px-16 py-20 md:py-24">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-16">
            <div className="lg:col-span-4">
              <Reveal>
                <p className="eyebrow">The Stop</p>
              </Reveal>
              <Reveal delay={120}>
                <h2 className="mt-5 font-heading text-2xl leading-tight text-foreground-950 md:text-3xl">
                  {isTbc ? "This one is still being written." : stop.destination}
                </h2>
              </Reveal>
            </div>
            <div className="lg:col-span-7 lg:col-start-6">
              <Reveal delay={140}>
                <p className="font-body text-base leading-relaxed text-foreground-700 md:text-lg">
                  {stop.description ??
                    (isTbc
                      ? "The details for this stop are still to be confirmed. Check back soon — the photography, the story and the full itinerary will appear here."
                      : "The photography, the story and the full itinerary for this stop will be published here as the journey unfolds.")}
                </p>
              </Reveal>

              <div className="mt-12 divide-y divide-foreground-950/12 border-t border-foreground-950/12">
                {[
                  { k: "Photography", icon: "ri-camera-lens-line" },
                  { k: "Video", icon: "ri-vidicon-line" },
                  { k: "Related Story", icon: "ri-book-open-line" },
                ].map((row) => (
                  <Reveal key={row.k} delay={180}>
                    <div className="flex items-center justify-between gap-6 py-5">
                      <span className="inline-flex items-center gap-3 font-body text-sm text-foreground-600">
                        <i className={`${row.icon} text-base text-primary-500`} />
                        {row.k}
                      </span>
                      <span className="font-label text-[10px] uppercase tracking-label text-foreground-400">
                        Coming soon
                      </span>
                    </div>
                  </Reveal>
                ))}
              </div>

              <Reveal delay={260}>
                <div className="mt-10 flex flex-col gap-4 sm:flex-row sm:items-center">
                  <Link to="/invite-eddie" className="btn-primary">
                    Invite Eddie
                    <i className="ri-arrow-right-line text-base" />
                  </Link>
                  <Link
                    to="/hire-eddie"
                    className="inline-flex items-center justify-center gap-3 whitespace-nowrap rounded-md border border-foreground-950/25 px-8 py-4 font-label text-[11px] uppercase tracking-label text-foreground-950 transition-all duration-500 hover:bg-foreground-950 hover:text-background-50"
                  >
                    Hire Eddie
                  </Link>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-background-100">
        <div className="w-full px-5 md:px-10 lg:px-16 py-14 md:py-16">
          <Reveal>
            <div className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-center">
              <div>
                <p className="eyebrow">Next Stop</p>
                <p className="mt-3 font-heading text-2xl text-foreground-950 md:text-3xl">
                  {next.dateShort} ·{" "}
                  <span className={next.status === "tbc" ? "italic" : ""}>
                    {next.destination}
                  </span>
                </p>
              </div>
              <Link
                to={`/culinary-journeys/${next.slug}`}
                className="inline-flex items-center justify-center gap-3 whitespace-nowrap rounded-md border border-foreground-950/25 px-8 py-4 font-label text-[11px] uppercase tracking-label text-foreground-950 transition-all duration-500 hover:bg-foreground-950 hover:text-background-50"
              >
                Continue the Journey
                <i className="ri-arrow-right-line text-base" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <CtaBand
        eyebrow="All Journeys"
        title={
          <>
            There are more
            <br />
            <span className="italic text-primary-400">than one road.</span>
          </>
        }
        text="Head back to the full travel journal, or start a conversation about the next journey."
        primary={{ label: "Back to Journeys", to: "/culinary-journeys" }}
        secondary={{ label: "Submit a Locavore", to: "/submit-a-locavore" }}
      />
    </SiteLayout>
  );
}